import bridge from '@/utils/bridge';
import events from './events';

const modifiersAvailable = ['ctrlKey', 'metaKey', 'altKey', 'shiftKey'];

function isModifiersSame(eventModifiers, browserEvent) {
  const eventModifiersSet = new Set(eventModifiers);
  const modifiersAvailableSet = new Set(modifiersAvailable);

  const iterator = modifiersAvailableSet.values();
  for (let i = iterator.next(); !i.done; i = iterator.next()) {
    if (eventModifiersSet.has(i.value) !== browserEvent[i.value]) {
      return false;
    }
  }
  return true;
}

class KeyBoardEvents {
  subscriptions = {
    onKeyDown: [],
    onKeyUp: [],
    onKeyPress: [],
  };

  eventTypes = Object.keys(this.subscriptions);

  constructor() {
    document.addEventListener('keypress', this.onKeyPress);
    document.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('keydown', this.onKeyDown);

    bridge.subscribe('keyboardEvents', this._onKeyDown);
  }

  /**
   * Subscribes to a Keyboard event
   * @param {String} eventId - The `id` of the action you need a shortcut for. Mentioned in `src/utils/eventIds.js`
   * @param {Function} callback - The callback function to be executed on shortcut
   * @param {String} eventType(optional) - When the callback needs to be executed - has to be
   * one of the three - `onKeyDown`, `onKeyUp`, `onKeyPress`
   * @returns {Function} The unsubscription callback to this subscription.
   * Needs to be called when this callback is no longer needed.
   */
  subscribe = (eventId, callback, eventType = 'onKeyDown') => {
    if (!events[eventId]) {
      const errorMsg = `Event ${eventId} is not available. Are you sure you've added it to src/utils/eventIds.js &
      src/utils/eventsDefinition`;
      console.warn(errorMsg);
      return new Error(errorMsg);
    }

    if (this.eventTypes.indexOf(eventType) < 0) {
      const errorMsg = `Events of type ${eventType} are not being handled here.`;
      console.warn(errorMsg);
      return new Error(errorMsg);
    }

    this.subscriptions[eventType].push({
      event: events[eventId],
      callback,
    });
    return () => {
      this.unsubscribe(eventId, callback, eventType);
    };
  };

  /**
   * Removes the Keyboard event callback. Should be done in `componentWillUnmount` or when you're sure that the
   * keyboard event handler is no longer needed.
   * @param {string} eventId - The `id` of the action you need a shortcut for. Mentioned in `src/utils/eventIds.js`
   * @param {function} callback - The callback reference to be unsubscribed
   * @param {string} eventType - Has to be of one of the three - `onKeyDown`, `onKeyUp`, `onKeyPress`
   */
  unsubscribe = (eventId, callback, eventType = 'onKeyDown') => {
    if (!events[eventId]) {
      console.warn(`Event ${eventId} is not available.`);
      return;
    }

    if (this.eventTypes.indexOf(eventType) < 0) {
      console.warn(`Events of type ${eventType} are not being handled here.`);
      return;
    }

    this.subscriptions[eventType] = this.subscriptions[eventType].filter(
      sub => {
        if (sub.event === events[eventId] && sub.callback === callback) {
          return false;
        }
        return true;
      }
    );
  };

  isValidEvent = (browserEvent, event) => {
    const {
      _props: { modifiers, allowedKeyCodes },
    } = event;

    if (allowedKeyCodes && allowedKeyCodes.indexOf(browserEvent.keyCode) < 0) {
      return false;
    }

    if (Array.isArray(modifiers)) {
      return isModifiersSame(modifiers, browserEvent);
    }
    return true;
  };

  publishEvent(browserEvent, subscriptions) {
    for (let i = 0; i < subscriptions.length; i += 1) {
      const subscription = subscriptions[i];
      if (this.isValidEvent(browserEvent, subscription.event)) {
        subscription.callback(browserEvent);
      }
    }
  }

  _onKeyDown = psuedoBrowserEvent => {
    this.publishEvent(psuedoBrowserEvent, this.subscriptions.onKeyDown);
  };

  onKeyDown = event => {
    this.publishEvent(event, this.subscriptions.onKeyDown);
  };

  onKeyUp = event => {
    this.publishEvent(event, this.subscriptions.onKeyUp);
  };

  onKeyPress = event => {
    this.publishEvent(event, this.subscriptions.onKeyPress);
  };
}

const keyboardEvents = new KeyBoardEvents();
export const { subscribe, unsubscribe } = keyboardEvents;



// WEBPACK FOOTER //
// ./src/utils/KeyboardShortcuts/keyboardShortcuts.js